import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { useSiteSettings } from '@/settings/useSiteSettings';
import { isExternalHref, resolveDashboardHref } from '@/services/siteSettings';

function Avatar({ name, avatarUrl }: { name: string; avatarUrl?: string | null }) {
  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={name}
        className="w-8 h-8 rounded-full object-cover shrink-0"
      />
    );
  }

  return (
    <div className="w-8 h-8 rounded-full bg-brand-600 flex items-center justify-center text-white text-sm font-semibold shrink-0">
      {name.charAt(0).toUpperCase()}
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const mobileRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const { user, profile, role, signOut } = useAuth();
  const { settings } = useSiteSettings();

  const isLoggedIn = Boolean(user);
  const displayName = profile?.name || user?.email || 'Account';
  const displayEmail = profile?.email || user?.email || '';
  const dashboardPath = role === 'admin' ? '/admin' : '/dashboard';
  const navLinks = settings.header.menuItems.filter((link) => link.enabled);
  const logoUrl = settings.header.logoUrl || settings.general.logoUrl;
  const brandText = settings.header.brandText || settings.general.shortName;
  const profileMenuItems = settings.header.profileMenuItems
    .filter((item) => item.enabled)
    .map((item) => ({
      ...item,
      href: resolveDashboardHref(item.href, dashboardPath),
    }));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setProfileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false);
      }
    }
    if (profileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileOpen]);

  const handleSignOut = async () => {
    setProfileOpen(false);
    setMobileOpen(false);
    await signOut();
    navigate('/login', { replace: true });
  };

  const handleHref = (href: string) => {
    if (isExternalHref(href)) {
      window.location.href = href;
      return;
    }
    navigate(href);
  };

  const isHome = location.pathname === '/';
  const isLight = settings.header.style === 'light'
    ? true
    : settings.header.style === 'dark'
    ? false
    : scrolled || !isHome;
  const headerSurfaceClass = settings.header.style === 'dark'
    ? 'bg-surface-900/95 backdrop-blur-md shadow-sm border-b border-surface-800'
    : isLight
    ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-surface-200'
    : 'bg-transparent';

  return (
    <header
      className={`${settings.header.sticky ? 'fixed top-0 left-0 right-0' : 'relative'} z-50 transition-all duration-300 ${headerSurfaceClass}`}
    >
      <nav className="flex items-center justify-between px-4 md:px-6 py-3 md:py-4">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 shrink-0">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={settings.general.storeName}
              className="w-8 h-8 rounded object-contain"
            />
          ) : (
            <span className="w-8 h-8 rounded bg-brand-600 text-white text-sm font-bold flex items-center justify-center">
              {brandText.charAt(0).toUpperCase()}
            </span>
          )}
          <span className={`font-semibold text-base md:text-lg tracking-tight ${isLight ? 'text-surface-900' : 'text-white'}`}>
            {brandText}
          </span>
        </Link>

        {/* Desktop nav links */}
        <div className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            isExternalHref(link.href) ? (
            <a
              key={link.label}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                isLight
                  ? 'text-surface-600 hover:text-surface-900 hover:bg-surface-100'
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              {link.label}
            </a>
            ) : (
            <Link
              key={link.label}
              to={link.href}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                isLight
                  ? 'text-surface-600 hover:text-surface-900 hover:bg-surface-100'
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              {link.label}
            </Link>
            )
          ))}
        </div>

        {/* Desktop right side */}
        <div className="hidden md:flex items-center gap-2">
          {isLoggedIn ? (
            <div ref={profileRef} className="relative">
              <button
                onClick={() => setProfileOpen((v) => !v)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${
                  isLight ? 'hover:bg-surface-100' : 'hover:bg-white/10'
                }`}
              >
                <Avatar name={displayName} avatarUrl={profile?.avatarUrl} />
                <span className={`text-sm font-medium ${isLight ? 'text-surface-700' : 'text-white'}`}>
                  {displayName}
                </span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform ${profileOpen ? 'rotate-180' : ''} ${isLight ? 'text-surface-500' : 'text-white/70'}`} />
              </button>

              {profileOpen && (
                <div className="absolute right-0 top-full mt-2 w-60 bg-white rounded-xl shadow-xl border border-surface-200 overflow-hidden z-50">
                  {/* User info */}
                  <div className="px-4 py-3 border-b border-surface-100 flex items-center gap-3">
                    <Avatar name={displayName} avatarUrl={profile?.avatarUrl} />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-surface-900 truncate">{displayName}</p>
                      <p className="text-xs text-surface-500 truncate">{displayEmail}</p>
                      {role && (
                        <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded text-xs font-medium bg-brand-50 text-brand-700 capitalize">{role}</span>
                      )}
                    </div>
                  </div>

                  {/* Menu items */}
                  <div className="py-1">
                    {profileMenuItems.map((item) => (
                      <Link
                        key={item.href}
                        to={item.href}
                        onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-surface-700 hover:bg-surface-50 transition-colors"
                      >
                        <i className={`${item.icon} text-surface-400`} />
                        {item.label}
                      </Link>
                    ))}
                  </div>

                  {/* Sign out */}
                  <div className="border-t border-surface-100 py-1">
                    <button
                      onClick={handleSignOut}
                      className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <i className="ri-logout-box-r-line" />
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              <button
                onClick={() => handleHref(settings.header.loggedOutSecondaryHref)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  isLight
                    ? 'text-surface-600 hover:text-surface-900'
                    : 'text-white/90 hover:text-white'
                }`}
              >
                {settings.header.loggedOutSecondaryLabel}
              </button>
              <button
                onClick={() => handleHref(settings.header.loggedOutPrimaryHref)}
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-brand-600 hover:bg-brand-700 text-white transition-colors whitespace-nowrap"
              >
                {settings.header.loggedOutPrimaryLabel}
              </button>
            </>
          )}
        </div>

        {/* Mobile hamburger */}
        <button
          className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg ${
            isLight ? 'text-surface-700' : 'text-white'
          }`}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl`} />
        </button>
      </nav>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          ref={mobileRef}
          className="md:hidden bg-white border-b border-surface-200 shadow-lg"
        >
          <div className="px-4 py-3 flex flex-col gap-1">
            {navLinks.map((link) => (
              isExternalHref(link.href) ? (
              <a
                key={link.label}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-2.5 rounded-md text-sm font-medium text-surface-700 hover:bg-surface-100"
              >
                {link.label}
              </a>
              ) : (
              <Link
                key={link.label}
                to={link.href}
                className="px-3 py-2.5 rounded-md text-sm font-medium text-surface-700 hover:bg-surface-100"
              >
                {link.label}
              </Link>
              )
            ))}

            <div className="border-t border-surface-200 mt-2 pt-2">
              {isLoggedIn ? (
                <>
                  {/* User info row */}
                  <div className="flex items-center gap-3 px-3 py-2.5 mb-1">
                    <Avatar name={displayName} avatarUrl={profile?.avatarUrl} />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-surface-900 truncate">{displayName}</p>
                      <p className="text-xs text-surface-500 truncate">{displayEmail}</p>
                    </div>
                  </div>
                  {profileMenuItems.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-surface-700 hover:bg-surface-100"
                    >
                      <i className={`${item.icon} text-surface-400`} />
                      {item.label}
                    </Link>
                  ))}
                  <button
                    onClick={handleSignOut}
                    className="flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm font-medium text-red-600 hover:bg-red-50 mt-1"
                  >
                    <i className="ri-logout-box-r-line" />
                    Sign Out
                  </button>
                </>
              ) : (
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => handleHref(settings.header.loggedOutSecondaryHref)}
                    className="px-3 py-2.5 rounded-md text-sm font-medium text-surface-700 hover:bg-surface-100 text-left"
                  >
                    {settings.header.loggedOutSecondaryLabel}
                  </button>
                  <button
                    onClick={() => handleHref(settings.header.loggedOutPrimaryHref)}
                    className="px-3 py-2.5 rounded-md text-sm font-semibold bg-brand-600 text-white text-center"
                  >
                    {settings.header.loggedOutPrimaryLabel}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
